import { ArrowRight, CheckCircle2, ClipboardList, XCircle } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import OfferInterestSignup from "@/components/public/OfferInterestSignup";
import OpportunityIntakeForm from "@/components/public/OpportunityIntakeForm";
import { TrackedAnchor, TrackedLink } from "@/components/public/TrackedCta";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { EntryPointConfig } from "@/lib/opportunityPayload";

interface EntryPointPageProps {
  config: EntryPointConfig;
  outcomes: string[];
  proofPoints: string[];
  examples: string[];
  notFit: string[];
  triagePrompts: string[];
}

const EntryPointPage = ({ config, outcomes, proofPoints, examples, notFit, triagePrompts }: EntryPointPageProps) => {
  return (
    <PublicLayout>
      <SEO
        title={`${config.title} - Shock Value Media`}
        description={config.description}
        url={config.path}
      />

      <section className="svm-ink-grid relative overflow-hidden bg-[#080b0f] py-12 text-white md:py-16">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-rose-950/25 to-transparent" />
        <div className="container relative mx-auto grid gap-10 px-4 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:px-8">
          <div className="max-w-4xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">
              Shock Value Media / {config.eyebrow}
            </p>
            <h1 className="svm-display-tight mb-6 text-5xl leading-[0.9] tracking-[-0.025em] md:text-7xl">{config.title}</h1>
            <p className="max-w-3xl text-lg font-medium leading-8 text-slate-300 md:text-xl">{config.description}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <TrackedAnchor
                href="#opportunity-intake"
                tracking={{
                  label: "Start with this path",
                  destination: `${config.path}#opportunity-intake`,
                  placement: "entry_hero_primary",
                  service: config.opportunityType,
                }}
                className="sm:inline-flex"
              >
                <Button size="lg" className="w-full rounded-md bg-amber-300 px-6 font-bold text-slate-950 shadow-[0_0_40px_rgba(251,191,36,0.2)] hover:bg-amber-200 sm:w-auto">
                  Start with this path
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </TrackedAnchor>
              <TrackedLink
                to="/project-planner"
                tracking={{
                  label: "I am not sure yet",
                  destination: "/project-planner",
                  placement: "entry_hero_secondary",
                  service: "project_translator",
                }}
                className="sm:inline-flex"
              >
                <Button size="lg" variant="outline" className="w-full rounded-md border-white/30 bg-white/5 px-6 font-bold text-white backdrop-blur hover:bg-white hover:text-slate-950 sm:w-auto">
                  I am not sure yet
                </Button>
              </TrackedLink>
            </div>
            <p className="mt-5 text-sm font-medium text-slate-400">
              No polished brief required. A person at SVM reviews every request.
            </p>
          </div>

          <aside className="border-y border-white/15 py-6 lg:border-l lg:border-y-0 lg:py-2 lg:pl-10" aria-label="What this path helps clarify">
            <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">The first conversation should clarify</p>
            <div className="grid gap-0">
              {outcomes.map((outcome, index) => (
                <div key={outcome} className="grid grid-cols-[34px_1fr] gap-3 border-t border-white/15 py-4 first:border-t-0 first:pt-0">
                  <span className="font-mono text-xs font-bold text-amber-300">0{index + 1}</span>
                  <p className="text-sm font-semibold leading-6 text-slate-200">{outcome}</p>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="border-b border-slate-300 bg-white py-14 md:py-20">
        <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[1.05fr_0.95fr] lg:px-8">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">Is this the right path?</p>
            <h2 className="svm-display-tight mb-6 text-4xl md:text-5xl">This is probably a fit if...</h2>
            <div className="border-t border-slate-300">
              {proofPoints.map((point) => (
                <div key={point} className="flex gap-3 border-b border-slate-300 py-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                  <p className="font-semibold leading-7 text-slate-700">{point}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="border-l-4 border-amber-300 bg-[#f6f2ea] p-6 md:p-8">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">What happens first</p>
            <h2 className="svm-display-tight text-3xl md:text-4xl">Start with a useful decision, not a full build.</h2>
            <p className="mt-5 font-medium leading-7 text-slate-700">
              SVM reviews the context, identifies the first constraint worth addressing, and recommends a bounded next step. Detailed scope comes after the problem and fit are clear.
            </p>
            <TrackedAnchor
              href="#opportunity-intake"
              tracking={{
                label: "Share the situation",
                destination: `${config.path}#opportunity-intake`,
                placement: "entry_decision_panel",
                service: config.opportunityType,
              }}
              className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-slate-950 underline decoration-amber-400 decoration-2 underline-offset-4 hover:text-rose-700"
            >
              Share the situation
              <ArrowRight className="h-4 w-4" />
            </TrackedAnchor>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-300 bg-[#f7f2e8] py-14 md:py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mb-10 max-w-3xl">
            <h2 className="svm-display-tight mb-4 text-4xl md:text-5xl">What this kind of work looks like</h2>
            <p className="font-medium text-slate-600">
              This site asks for enough context to have a useful conversation. If the work is a fit, detailed planning,
              decisions, and progress stay organized in SVM Operator.
            </p>
          </div>

          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <div className="mb-4 flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                <h3 className="text-xl font-bold text-slate-950">Examples</h3>
              </div>
              <div className="border-t border-slate-300">
                {examples.map((example) => (
                  <div key={example} className="border-b border-slate-300 py-4 text-sm font-semibold leading-6 text-slate-700">
                    {example}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-4 flex items-center gap-2 text-slate-500">
                <XCircle className="h-5 w-5" />
                <h3 className="text-xl font-bold text-slate-950">Probably not the right fit</h3>
              </div>
              <div className="border-t border-slate-300">
                {notFit.map((item) => (
                  <div key={item} className="border-b border-slate-300 py-4 text-sm font-semibold leading-6 text-slate-600">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12 border-t border-slate-300 pt-8">
            <div className="mb-5 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-emerald-700" />
              <h3 className="text-xl font-bold">Helpful details</h3>
            </div>
            <p className="mb-5 max-w-3xl text-sm font-medium text-slate-600">
              Rough details are fine. The goal is to understand the problem and choose a sensible first step, not make you write a technical brief.
            </p>
            <div className="grid border-y border-slate-300 md:grid-cols-3">
              {triagePrompts.map((prompt) => (
                <div key={prompt} className="border-b border-slate-300 py-4 text-sm font-bold leading-6 text-slate-700 last:border-b-0 md:border-b-0 md:border-r md:px-5 md:last:border-r-0">
                  {prompt}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div id="opportunity-intake">
        <OpportunityIntakeForm config={config} />
      </div>

      <OfferInterestSignup config={config} />
    </PublicLayout>
  );
};

export default EntryPointPage;
